import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PhoneCall, ChevronDown, ChevronUp, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePaidLine } from "@/hooks/usePaidLine";
import { PaidLineNumber } from "@/components/PaidLineNumber";

const LS_KEY = "callInBannerCollapsed";

const CollapsedBanner = ({ toggle, navigate, paidLine }) => (
  <div data-testid="callin-banner-collapsed"
    className="shrink-0 border-b border-border bg-[#0A0A0A] px-6 py-2 flex items-center gap-3">
    <PhoneCall className="w-4 h-4 text-[#007AFF] shrink-0" />
    <p className="text-sm min-w-0 truncate flex items-center gap-1">
      <span className="text-muted-foreground">Call to file a report: </span>
      <PaidLineNumber paidLine={paidLine} size="sm" />
      <span className="text-muted-foreground hidden sm:inline"> — paid line, credits required</span>
    </p>
    <button onClick={() => navigate("/credits")} data-testid="callin-buy-credits-link"
      className="ml-auto shrink-0 text-xs font-semibold text-[#007AFF] hover:underline">Buy Credits</button>
    <button onClick={toggle} data-testid="callin-expand-btn" aria-label="Expand call-in info"
      className="shrink-0 text-muted-foreground hover:text-white transition-colors duration-200">
      <ChevronDown className="w-4 h-4" />
    </button>
  </div>
);

export const CallInBanner = () => {
  const navigate = useNavigate();
  const paidLine = usePaidLine();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(LS_KEY) === "1");

  const toggle = () => {
    setCollapsed((c) => {
      localStorage.setItem(LS_KEY, c ? "0" : "1");
      return !c;
    });
  };

  if (collapsed) return <CollapsedBanner toggle={toggle} navigate={navigate} paidLine={paidLine} />;

  return (
    <div data-testid="callin-banner" className="shrink-0 border-b border-border bg-[#0A0A0A] px-6 py-4">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="shrink-0">
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground mb-1">Call to file a report</p>
          <PaidLineNumber paidLine={paidLine} size="lg" />
        </div>
        <div className="flex-1 min-w-0 text-xs text-muted-foreground leading-relaxed lg:px-4 lg:border-l lg:border-border">
          <p>
            <span className="text-white font-medium">This is a paid line</span> — calls are enabled by purchasing
            credits, but your records always stay on this site unless you delete them. With the paid service you'll be
            talking to a <span className="text-white font-medium">voice avatar</span>: an AI phone agent (built on
            Vapi) that answers like a real person, asks the right follow-up questions, and files the structured report,
            transcript, and audio recording straight to your account.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" onClick={() => navigate("/credits")} data-testid="callin-buy-credits-btn"
            className="bg-[#007AFF] hover:bg-[#0066d6] text-white rounded-sm gap-2 font-semibold text-xs">
            <CreditCard className="w-3.5 h-3.5" /> Buy Credits
          </Button>
          <button onClick={toggle} data-testid="callin-collapse-btn" aria-label="Collapse call-in info"
            className="text-muted-foreground hover:text-white transition-colors duration-200 p-1">
            <ChevronUp className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
